/**
 * @fileoverview Reusable drawer container with consistent styling and width configuration.
 */

import type { ReactNode } from 'react';
import { Box, Drawer, type DrawerProps, type SxProps, type Theme } from '@mui/material';

export interface DrawerContainerProps extends Omit<DrawerProps, 'children'> {
  /**
   * Drawer content
   */
  children: ReactNode;
  /**
   * Width of the drawer in pixels
   */
  width: number;
  /**
   * Maximum width as viewport percentage (e.g., '90vw')
   * @default '90vw'
   */
  maxWidth?: string;
  /**
   * Top padding/margin (useful for avoiding app bars)
   * @default 6
   */
  topSpacing?: number;
  /**
   * Horizontal padding for content
   * @default 2
   */
  contentPadding?: number;
  /**
   * Additional sx props for the content container
   */
  contentSx?: SxProps<Theme>;
}

/**
 * Standardized drawer container with configurable width and content padding.
 * Provides consistent drawer styling across the application.
 */
export function DrawerContainer({
  children,
  width,
  maxWidth = '90vw',
  topSpacing = 6,
  contentPadding = 2,
  contentSx,
  ...drawerProps
}: DrawerContainerProps) {
  return (
    <Drawer
      {...drawerProps}
      sx={{
        '& .MuiDrawer-paper': {
          width,
          maxWidth,
        },
        ...drawerProps.sx,
      }}
    >
      <Box sx={{ p: contentPadding, mt: topSpacing, ...contentSx }}>
        {children}
      </Box>
    </Drawer>
  );
}
